import './ImageUpload.css';

import { Box, Button, CircularProgress } from '@material-ui/core';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { useCustomTheme } from '../../../../../../../hooks/useCustomTheme';
import { convertFileToBase64 } from '../../../../../../../utils/convertFileToBase64';
import { Font } from '../../../../../../shared/components/Font';
import { Text } from '../../../../../../shared/components/Text';
import { ImageFile } from '../types/ImageFile';

export const ImageUpload: React.FC<ImageUpload> = ({
  name,
  onImageChange,
  textId = 'CHOOSE_IMAGE_FILE',
  defaultValue = undefined,
  loading = false,
}) => {
  const { customBorderRadius } = useCustomTheme();
  const [imageFile, setImageFile] = useState<ImageFile | undefined>(defaultValue);

  useEffect(() => {
    setImageFile(defaultValue);
  }, [defaultValue]);

  const addFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const value: File = e.target.files[0];
    const result = await convertFileToBase64(value);
    const imageFile: ImageFile = {
      base64: result,
      file: value,
      name: value.name,
      size: value.size,
      mimeType: value.type,
    };
    setImageFile(imageFile);
    onImageChange(imageFile);
  };

  const deleteFile = () => {
    setImageFile(undefined);
    onImageChange(undefined);
  };

  const imageFileSize = imageFile ? imageFile.size / 1000 : undefined;

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'}>
      <Box className={'image-container'} bgcolor={'grey.50'} borderRadius={customBorderRadius.paper}>
        {loading ? (
          <CircularProgress color={'secondary'} size={20} />
        ) : imageFile ? (
          <img
            src={imageFile.base64}
            width={'auto'}
            size={'auto'}
            alt={'merchant-brand-logo'}
            style={{ maxHeight: '165px', maxWidth: '100%' }}
          />
        ) : (
          <>
            <input
              value={''}
              type="file"
              accept="image/png"
              id={name}
              name={name}
              key={name}
              disabled={false}
              style={{
                display: 'none',
              }}
              onChange={addFile}
            />
            <label htmlFor={name}>
              <Button
                disableElevation
                fullWidth
                color={'default'}
                variant={'contained'}
                component={'span'}
                startIcon={<i className={'material-icons-outlined'}>upload</i>}
              >
                <Text tId={textId} />
              </Button>
            </label>
          </>
        )}
      </Box>
      <Box mt={1} width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <Box>
          {imageFileSize && (
            <Box
              display={'flex'}
              alignItems={'center'}
              gridGap={4}
              color={imageFileSize > 80 ? 'error.main' : 'text.secondary'}
            >
              <i className={'material-icons-outlined'} style={{ fontSize: 14 }}>
                {imageFileSize > 80 ? 'error_outline' : 'check_circle'}
              </i>
              <Font variant={'caption'}>
                <Text tId={'IMAGE_SIZE'} />
                {`: ${imageFileSize.toFixed(1)} Kb`}
              </Font>
            </Box>
          )}
        </Box>
        <Button
          disableElevation
          size={'small'}
          color={'default'}
          variant={'text'}
          disabled={!imageFile || loading}
          startIcon={<i className={'material-icons-outlined'}>delete</i>}
          onClick={deleteFile}
        >
          <Text tId={'DELETE'} />
        </Button>
      </Box>
    </Box>
  );
};

interface ImageUpload {
  name: string;
  onImageChange: (imageFile: ImageFile | undefined) => void;
  defaultValue?: ImageFile;
  disabled?: boolean;
  loading?: boolean;
  textId?: string;
}
